// ════════════════════════════════════════════════════════════
//  Hud.jsx — everything you read during a fight (SHATTERPROOF)
// ════════════════════════════════════════════════════════════
//  Repaints at 10 Hz from a snapshot of the engine. It never touches
//  the Canvas (the scene is memo'd and reads refs).
//
//  CONTROLS — free aim: the mouse is your aim for EVERYTHING.
//   W A S D      move your being (let go and it holds its ground)
//   Left click   Lyt Attack (one per 3 s)
//   Space        Primera — your Ilk's dodge (5 s)
//   1 2 3        Impera I · II · III        4  U'hlti (Lord)
//   Z X C        your three Tomes (10 s each)
//   T            Transcend (15 / 30 Khao)   Tab  bench: switch & resurrect
//   Right-drag   orbit the camera · wheel zoom · R camera default
//   G auto · P pause · M music · H clear the screen (hide every word and bar)
//  Every discrete key ignores auto-repeat (e.repeat): one press, one call.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef, useState, useCallback } from "react";
import { LINES, TIER_LABEL, TIER_ORDER, IMPERA_UNLOCK, PRIMERA, portraitUrl } from "../data/roster.js";
import { ARTS, STATUS, CAST_TIME, tomeUrl } from "../data/arts.js";
import {
  artReady, maxLyf, lytOf, KHAO_IDLE, TRANSCEND_KHAO, TRANSCEND_OPENS, SWAP_KHAO, RES_TALLY, RES_FARE,
  OVERTIME, CALL_TIME, RIFT_ON, AUTO_SEND, LYT_COOLDOWN, PRIMERA_COOLDOWN, STYLES, STYLE_IDS,
} from "../sim/battle.js";
import FrontRow from "./FrontRow.jsx";
import WorldChat from "./WorldChat.jsx";
import { issue } from "../sim/issue.js";
import HideBtn from "./HideBtn.jsx";
import Callouts from "./Callouts.jsx";
import "./plan.css";   // the bench's Plan row   // LYRAEI PLAYS: a trainer's calls under the clock
import { say } from "../data/text.js";
import { PIECES, useHudView, hudClass, toggle, toggleClear, showAll } from "./hudView.js";
import { layoutDemo } from "../ieLayout.js";
import { stepBanners } from "./bannerQueue.js";
import bannerCfg from "../data/banners.json";   // the Sun Dial's Banners page: colours and caP'n's own banners (bannerLook.js)
import { colorFor, graceFor, customBanners, toQueued, firesOn } from "./bannerLook.js";
const CUSTOM = customBanners(bannerCfg);

const castLabel = (lvl) => (CAST_TIME[lvl] ? `${CAST_TIME[lvl]}s cast` : "instant");

function snapshot(b, mySide) {
  return {
    t: b.t, over: b.over, overtime: !!b.overtime, callTime: b.callTime || CALL_TIME,
    sides: b.sides.map((s) => {
      const f = s.active >= 0 ? s.roster[s.active] : null;
      const regen = b.t - s.lastSpendT >= KHAO_IDLE;
      return {
        side: s.side, name: s.name, khao: s.khao, khaoMax: s.khaoMax, khaoRegen: regen && s.khao < s.khaoMax,
        khaoWait: Math.max(0, KHAO_IDLE - (b.t - s.lastSpendT)),
        auto: s.auto, style: s.style || "balanced", swapLock: s.swapLock, resCd: s.resCd,
        waiting: s.active < 0 && s.roster.some((r) => !r.ruined), pendingNext: s.pendingNext,
        roster: s.roster.map((r) => ({ idx: r.idx, line: r.line, tier: r.tier, anima: r.anima, maxAnima: r.maxAnima, lyf: r.lyf, maxLyf: maxLyf(r), ruined: r.ruined, resLeft: RES_TALLY[r.tier] - r.resUsed })),
        tomes: s.tomes.map((t) => ({ id: t.id, cd: t.cd })),
        active: f ? {
          uid: f.uid, idx: f.idx, line: f.line, tier: f.tier, anima: f.anima, maxAnima: f.maxAnima, lyf: f.lyf, maxLyf: maxLyf(f), lyt: lytOf(f),
          lytCd: f.lytCd, lytCharges: f.lytCharges, primeraCd: f.primeraCd, state: f.state,
          statuses: Object.keys(f.statuses).filter((k) => STATUS[k]), treasures: Object.keys(f.treasures),
          ready: s.side === mySide ? {
            impera: LINES[f.line].impera.map((id, i) => artReady(b, f, id, { impera: i })),
            uhlti: artReady(b, f, LINES[f.line].uhlti, { uhlti: true }),
            tomes: s.tomes.map((t) => artReady(b, f, t.id, { tome: t })),
          } : null,
        } : null,
      };
    }),
  };
}
const fmtTime = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

// ── a being's plate: Lyf (the shield, in pips) over Anima (the life) ──
function Plate({ s, mine, hideId }) {
  const f = s.active;
  if (!f) return (
    <div className={"plate " + (mine ? "mine" : "theirs")}>
      <div className="plate-wait">{s.waiting ? "choosing…" : ""}</div>
      <TeamRow s={s} />
      <HideBtn id={hideId} />
    </div>
  );
  const L = LINES[f.line], ti = TIER_ORDER.indexOf(f.tier);
  const af = Math.min(1, f.anima / f.maxAnima);   // a carried over-cap (THE CLIMB) fills the bar; the number says the rest
  const exposed = f.lyf < 0.999 || f.maxLyf === 0;
  return (
    <div className={"plate " + (mine ? "mine" : "theirs")}>
      <div className="plate-main">
        <div className={"portrait tier-" + f.tier} style={{ backgroundImage: `url(${portraitUrl(f.line, f.tier)})` }} />
        <div className="plate-body">
          <div className="plate-lord">{L.lord}</div>
          <div className="plate-title">{L.tiers[f.tier].title}</div>
          <div className="plate-tags">
            <span className="tierpips">{[0, 1, 2].map((i) => <i key={i} className={i <= ti ? "on" : ""}>◆</i>)}</span>
            <span className="kinilk">{L.kin} · {L.ilk}</span>
            <span className="lytv" title="Lyt — the power of its Lyt Attack">LYT {f.lyt}</span>
          </div>
          <div className="lyfpips" title="Lyf — the shield. Mends 1 per second after 5 s unhurt.">
            <span className="lbl">LYF</span>
            {f.maxLyf === 0 ? <em className="nolyf">traded for Lyt</em> : Array.from({ length: f.maxLyf }, (_, i) => (
              <i key={i} className={f.lyf >= i + 1 ? "on" : f.lyf > i ? "part" : ""} style={f.lyf > i && f.lyf < i + 1 ? { "--p": f.lyf - i } : null} />
            ))}
          </div>
          <div className={"bar animabar" + (exposed ? " exposed" : "")} title="Anima — its life. Exposed while Lyf is down. Only Arts restore it.">
            <div className="fill" style={{ width: `${af * 100}%` }} />
            <span className="bar-label">ANIMA {Math.ceil(f.anima)} / {f.maxAnima}{exposed ? " · EXPOSED" : ""}</span>
          </div>
          <div className="statuses">
            {f.statuses.map((k) => <span key={k} className="chip" style={{ borderColor: STATUS[k].color, color: STATUS[k].color }} title={STATUS[k].note}>{STATUS[k].label}</span>)}
            {f.treasures.map((k) => <span key={k} className="chip treasure" title="A Treasure Tome this being wields">{k.toUpperCase()}</span>)}
          </div>
        </div>
      </div>
      <TeamRow s={s} />
      <HideBtn id={hideId} />
    </div>
  );
}
function TeamRow({ s }) {
  return (
    <div className="team-row">
      <div className={"khaobar" + (s.khaoRegen ? " regen" : "")} title="Khao — shared by all three beings. After 5 s unspent it refills 3 per second.">
        <div className="fill" style={{ width: `${(s.khao / s.khaoMax) * 100}%` }} />
        <span className="bar-label">KHAO {Math.floor(s.khao)} / {s.khaoMax}{s.khaoRegen ? " ▲" : s.khaoWait > 0 && s.khao < s.khaoMax ? ` · ${s.khaoWait.toFixed(1)}s` : ""}</span>
      </div>
      <div className="pips">
        {s.roster.map((r) => (
          <span key={r.idx} className={"pip" + (r.ruined ? " ruined" : "") + (s.active && s.active.idx === r.idx ? " active" : "")}
            style={{ backgroundImage: `url(${portraitUrl(r.line, r.tier)})` }} title={`${LINES[r.line].tiers[r.tier].title} — Anima ${Math.ceil(r.anima)}`}>
            {!r.ruined && <b style={{ width: `${Math.min(100, (r.anima / r.maxAnima) * 100)}%` }} />}
          </span>
        ))}
      </div>
    </div>
  );
}

// one action button
function Act({ k, label, name, cost, costAnima, cd, cdMax, note, locked, ready, ult, tome, onClick, title, art }) {
  return (
    <button className={"move" + (ult ? " ult" : "") + (tome ? " tome" : "") + (locked ? " locked" : "") + (ready ? " ready" : "")}
      onClick={onClick} title={title} disabled={locked}>
      {art && <span className="tome-art" style={{ backgroundImage: `url(${art})` }} />}
      <span className="key">{k}</span>
      <span className="slotname">{label}</span>
      <span className="mname">{locked ? "🔒 " + locked : name}</span>
      {!locked && <span className="costs">{cost > 0 && <em className="kc">{cost} KHAO</em>}{costAnima > 0 && <em className="ac">{costAnima} ANIMA</em>}{note && <em className="nt">{note}</em>}</span>}
      {!locked && cd > 0 && <span className="cd" style={{ "--cd": cdMax ? Math.min(1, cd / cdMax) : 1 }}><b>{cd.toFixed(1)}</b></span>}
    </button>
  );
}

function ActionBar({ s, t, send }) {
  const f = s.active;
  if (!f || !f.ready) return <div className="movebar empty" />;
  const L = LINES[f.line], P = PRIMERA[L.ilk];
  const nextTier = TIER_ORDER[TIER_ORDER.indexOf(f.tier) + 1];
  const tc = nextTier ? TRANSCEND_KHAO[nextTier] : 0;
  const tWait = nextTier ? Math.max(0, TRANSCEND_OPENS[nextTier] - t) : 0;   // the Ascension clock
  const u = ARTS[L.uhlti];
  return (
    <div className="movebar">
      <HideBtn id="actions" />
      <div className="group">
        <Act k="LMB" label="Lyt Attack" name={L.lytName} note={`${f.lyt} Lyt${f.lytCharges ? ` · +${f.lytCharges}` : ""}`} cd={f.lytCharges ? 0 : f.lytCd} cdMax={LYT_COOLDOWN}
          ready={f.lytCd <= 0 || f.lytCharges > 0} onClick={() => send({ type: "lyt" })} title="Lyt Attack at your aim — power = this being's Lyt. One per 3 seconds." />
        <Act k="␣" label="Primera" name={P.name} cd={f.primeraCd} cdMax={PRIMERA_COOLDOWN} ready={f.primeraCd <= 0}
          onClick={() => send({ type: "primera" })} title={`${L.ilk} Primera — ${P.note}`} />
      </div>
      <div className="group">
        {L.impera.map((id, i) => {
          const a = ARTS[id], r = f.ready.impera[i];
          return <Act key={id} k={i + 1} label={`Impera ${"I".repeat(i + 1)} · ${castLabel(a.level)}`} name={a.name} cost={a.khao} costAnima={a.anima}
            locked={r.why === "locked" ? TIER_LABEL[IMPERA_UNLOCK[i]] : null} ready={r.ok} note={r.why === "imbued" ? "imbued" : null}
            onClick={() => send({ type: "impera", i })} title={a.card} />;
        })}
        <Act k="4" label={`U'hlti · instant`} name={u.name} cost={u.khao} costAnima={u.anima} ult
          locked={f.tier !== "lord" ? "Lord" : null} ready={f.ready.uhlti.ok} onClick={() => send({ type: "uhlti" })} title={u.card} />
      </div>
      <div className="group">
        {s.tomes.map((t, i) => {
          const a = ARTS[t.id], r = f.ready.tomes[i];
          return <Act key={t.id} k={"ZXC"[i]} label={`Tome · ${a.school} · ${castLabel(a.level)}`} name={a.name} cost={a.khao} costAnima={a.anima} tome
            cd={t.cd} cdMax={10} ready={r.ok} note={r.why === "imbued" ? "imbued" : null} art={tomeUrl(t.id)} onClick={() => send({ type: "tome", slot: i })} title={a.card} />;
        })}
      </div>
      <div className="group">
        <Act k="T" label="Transcend" name={nextTier ? `→ ${TIER_LABEL[nextTier]}` : "Lord"} cost={tc} locked={!nextTier ? "—" : null}
          cd={tWait} cdMax={nextTier ? TRANSCEND_OPENS[nextTier] : 0} note={tWait > 0 ? `opens ${fmtTime(TRANSCEND_OPENS[nextTier])}` : null}
          ready={!!nextTier && tWait <= 0 && s.khao >= tc} onClick={() => send({ type: "transcend" })} title={`Transcend: ${TRANSCEND_KHAO.champion} Khao to Champion (from ${fmtTime(TRANSCEND_OPENS.champion)}), ${TRANSCEND_KHAO.lord} Khao to Lord (from ${fmtTime(TRANSCEND_OPENS.lord)}).`} />
      </div>
    </div>
  );
}

// THE PLAN (caP'n 2026-10-17, yes to a plan button for players): how your side fights while it's on
// Auto — the same five STYLES the League NPCs and Lyraei pick from. Behaviour only; G turns Auto on/off.
function PlanRow({ s, onPlan }) {
  return (
    <div className="plan-row">
      <div className="plan-head">{say("plan.title")} <small>{s.auto ? say("plan.on") : say("plan.off")}</small></div>
      <div className="plan-chips">
        {STYLE_IDS.map((id) => (
          <button key={id} className={"plan-chip" + (s.style === id ? " on" : "")} onClick={() => onPlan(id)} aria-pressed={s.style === id} title={STYLES[id].blurb}>
            {STYLES[id].name}<small>{STYLES[id].blurb}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function Bench({ s, title, onSwap, onSend, onRes, onClose, countdown, onPlan }) {
  return (
    <div className="bench-overlay">
      <div className="bench">
        <div className="bench-title">{title}{countdown != null && <span className="count">{Math.max(0, Math.ceil(countdown))}</span>}</div>
        <div className="bench-row">
          {s.roster.map((r, i) => {
            const L = LINES[r.line];
            const isActive = s.active && s.active.idx === r.idx;
            return (
              <div key={r.idx} className={"bench-card" + (r.ruined ? " ruined" : "") + (isActive ? " active" : "")}>
                <span className="key">{i + 1}</span>
                <span className="bc-art" style={{ backgroundImage: `url(${portraitUrl(r.line, r.tier)})` }} />
                <span className="bc-name">{L.lord}</span>
                <span className="bc-title">{L.tiers[r.tier].title}</span>
                <span className="bc-hp"><i style={{ width: `${Math.min(100, (r.anima / r.maxAnima) * 100)}%` }} /></span>
                {!r.ruined && !isActive && onSwap && <button className="bc-btn" onClick={() => onSwap(r.idx)} disabled={s.khao < SWAP_KHAO[r.tier] || s.swapLock > 0}>Switch in · {SWAP_KHAO[r.tier]} Khao</button>}
                {!r.ruined && !isActive && onSend && <button className="bc-btn" onClick={() => onSend(r.idx)}>Send in</button>}
                {isActive && <span className="bc-note">on the field</span>}
                {r.ruined && <>
                  <span className="bc-ruined">IN THE NECROPOLIS</span>
                  {r.resLeft > 0 ? <>
                    <button className="bc-btn res" onClick={() => onRes(r.idx, "field")} disabled={s.khao < RES_FARE[r.tier] || s.resCd > 0}>Resurrect → field · {RES_FARE[r.tier]}</button>
                    <button className="bc-btn res" onClick={() => onRes(r.idx, "hand")} disabled={s.khao < RES_FARE[r.tier] || s.resCd > 0}>→ hand</button>
                    <span className="bc-note">{r.resLeft} return{r.resLeft === 1 ? "" : "s"} left{s.resCd > 0 ? ` · next rite ${Math.ceil(s.resCd)}s` : ""}</span>
                  </> : <span className="bc-note">no returns left</span>}
                </>}
              </div>
            );
          })}
        </div>
        {onPlan && <PlanRow s={s} onPlan={onPlan} />}
        {onClose && <button className="bench-close" onClick={onClose}>Tab / Esc — back to the fight</button>}
      </div>
    </div>
  );
}

// ── THE SCREEN EDITOR's "Make it happen" (dev only, caP'n 2026-10-21: "recreate when actions start happening so I can
//    see how and where they appear ... and move it around to fix the stacking"). Each scene shows real banners, frozen
//    mid-flight (they never slide away) until "Clear the banners", so they can be seen stacked and dragged.
//    Only the Banners list sees these: nothing reaches the battle, the log, the effects or the network.
const demoBanners = [];
let bannersOn = 0;   // a scene only plays while a fight's banners are on screen (never queued up for a later fight)
if (process.env.NODE_ENV !== "production") {
  let n = 0;
  const ids = Object.keys(LINES), A = ids[0], B = ids[1] || ids[0];
  const make = (kind, mine, line, tier, title, sub) => ({ key: "demo" + ++n, kind, mine, line, tier, title, sub, life: 600, hold: true });
  const uName = (l) => (ARTS[LINES[l].uhlti] && ARTS[LINES[l].uhlti].name) || "U'hlti";
  const ult = () => [make("ult", true, A, "lord", uName(A), `${LINES[A].lord} — U'hlti`), make("ult", false, B, "lord", uName(B), `${LINES[B].lord} — U'hlti`)];
  const ascend = () => [make("ascend", true, A, "champion", "TRANSCENDENCE", `${LINES[A].lord}: ${TIER_LABEL.beb} → ${TIER_LABEL.champion}`), make("ascend", false, B, "champion", "TRANSCENDENCE", `${LINES[B].lord}: ${TIER_LABEL.beb} → ${TIER_LABEL.champion}`)];
  const enter = () => [make("enter", true, A, "beb", `Go, ${LINES[A].lord}!`, LINES[A].tiers.beb.title), make("enter", false, B, "beb", `They send ${LINES[B].lord}`, LINES[B].tiers.beb.title)];
  const rift = () => [make("rift", false, null, null, "THE RIFT CLOSES", "Every being on the field bleeds Anima until it ends")];
  // live = real timing through the waiting line and the clash (watch it play); held = frozen on screen to arrange
  const live = (list) => list.map((x) => ({ ...x, hold: false, life: { ult: 2.2, ascend: 2.6, rift: 3, enter: 1.6 }[x.kind] }));
  const show = (list) => () => { if (bannersOn) demoBanners.push(...list); };
  layoutDemo("banners.live", "In a fight · everything at once, live", () => show(live([...ult(), ...ascend(), ...enter(), ...rift(), make("ult", true, A, "lord", uName(A), "a second one, waiting its turn")]))());
  layoutDemo("banners.ult", "In a fight · U'hlti clash (held)", () => show(ult())());
  layoutDemo("banners.ascend", "In a fight · Transcendence clash (held)", () => show(ascend())());
  layoutDemo("banners.enter", "In a fight · both send a being in (held)", () => show(enter())());
  layoutDemo("banners.solo", "In a fight · just yours (held)", () => show([ult()[0], enter()[0]])());
  layoutDemo("banners.rift", "In a fight · the Rift (held)", () => show(rift())());
  layoutDemo("banners.clear", "In a fight · clear the banners", () => show([{ clear: true }])());
  // caP'n's own banners (Sun Dial → Banners): one "live" scene (pops in and out for real) and one "held" scene each
  for (const c of CUSTOM) {
    layoutDemo(`banners.custom.${c.id}.live`, `Your banner · ${c.name} (live)`, () => show([toQueued(c, "demo" + ++n, false)])());
    layoutDemo(`banners.custom.${c.id}`, `Your banner · ${c.name} (held)`, () => show([toQueued(c, "demo" + ++n, true)])());
  }
}

function Banners({ battle, mySide }) {
  const [list, setList] = useState([]);
  const cursor = useRef(0);
  const q = useRef({ shown: [], waiting: [] });   // the lanes, the waiting line and the clash: bannerQueue.js
  useEffect(() => { bannersOn++; return () => { bannersOn--; }; }, []);
  useEffect(() => {
    const id = setInterval(() => {
      const add = [];
      let clear = false;
      while (demoBanners.length) { const d = demoBanners.shift(); if (d.clear) clear = true; else add.push(d); }   // dev only (empty otherwise)
      const b = battle.current;
      for (const e of b ? b.events : []) {
        if (e.seq <= cursor.current) continue;
        cursor.current = e.seq;
        const mine = e.side === mySide;
        for (const [when, ev] of [["ult", "ult"], ["ascend", "transcend_start"], ["enter", "enter"], ["returns", "resurrect"], ["rift", "overtime"]])
          if (e.type === ev && !(ev === "enter" && !(b.t > 0.2))) for (const c of CUSTOM) if (firesOn(c, when, mine)) add.push(toQueued(c, `${e.seq}-${c.id}`, false));
        if (e.type === "ult") add.push({ key: e.seq, kind: "ult", mine, line: e.line, tier: "lord", title: e.name, sub: `${LINES[e.line].lord} — U'hlti`, life: 2.2 });
        if (e.type === "transcend_start") add.push({ key: e.seq, kind: "ascend", mine, line: e.line, tier: e.to, title: "TRANSCENDENCE", sub: `${LINES[e.line].lord}: ${TIER_LABEL[e.from]} → ${TIER_LABEL[e.to]}`, life: 2.6 });
        if (e.type === "overtime") add.push({ key: e.seq, kind: "rift", title: "THE RIFT CLOSES", sub: "Every being on the field bleeds Anima until it ends", life: 3 });
        if (e.type === "enter" && b.t > 0.2) add.push({ key: e.seq, kind: "enter", mine, line: e.line, tier: e.tier, title: mine ? `Go, ${LINES[e.line].lord}!` : `${b.sides[e.side].name} sends ${LINES[e.line].lord}`, sub: LINES[e.line].tiers[e.tier].title, life: 1.6 });
        if (e.type === "resurrect") add.push({ key: e.seq, kind: "enter", mine: e.side === mySide, line: e.line, tier: e.tier, title: `${LINES[e.line].lord} returns`, sub: e.to === "hand" ? "Resurrected to the hand" : "Resurrected onto the field", life: 1.8 });
      }
      const st = q.current;
      if (clear) { st.shown = st.shown.filter((x) => !x.hold); st.waiting = st.waiting.filter((x) => !x.hold); }
      const r = stepBanners(st.shown, st.waiting, add, performance.now());
      q.current = { shown: r.shown, waiting: r.waiting };
      if (r.changed || clear) setList(r.shown);
    }, 100);
    return () => clearInterval(id);
  }, [battle, mySide]);
  const graceOf = (x) => (x.custom && x.grace) || (x.kind === "rift" ? null : graceFor(bannerCfg, x.mine, x.ownGrace, "cutin"));
  // the Rift belongs to neither player, so BOTH Graces light it: the left colour from the left, the right colour from the right
  const riftGrace = (x) => { if (x.kind !== "rift") return null; const l = graceFor(bannerCfg, true, null, "rift"), r = graceFor(bannerCfg, false, null, "rift"); return l || r ? { "--gl": l || r, "--gr": r || l } : null; };   // the player's glow (a bought colour will arrive as x.ownGrace)
  return (
    <div className="banners">
      {list.map((x) => (
        <div key={x.key} className={`banner ${x.kind} ${x.mine ? "mine" : "theirs"}${x.half ? " half" : ""}${x.custom ? ` custom ${x.motion}` : ""}${graceOf(x) ? " graced" : riftGrace(x) ? " rift-grace" : ""}`}
          style={{ ...(graceOf(x) ? { "--g": graceOf(x) } : riftGrace(x) || null), ...(x.hold ? { "--life": "2s", animationDelay: "-0.5s", animationPlayState: "paused" } : { "--life": x.secs + "s" }),
            ...(((x.custom && x.color) || colorFor(bannerCfg, x.custom ? "custom" : x.kind, x.kind === "rift" ? undefined : x.mine)) ? { "--c": (x.custom && x.color) || colorFor(bannerCfg, x.custom ? "custom" : x.kind, x.kind === "rift" ? undefined : x.mine) } : null) }}>
          {x.line && <span className="banner-art" style={{ backgroundImage: `url(${portraitUrl(x.line, x.tier || "beb")})` }} />}
          <span className="banner-text"><b>{x.title}</b><em>{x.sub}</em></span>
        </div>
      ))}
    </div>
  );
}

// THE FIGHT LOG AS BANNERS (caP'n 2026-10-21: "instead of regular plain text, they should be banners" — NOT flying in and out like the
// big cut-ins: they stay in the log's spot and fade with age). The colours are Terror's card-type colours (app-terror/src/theme.js TYPE_COLORS_SD).
const LOG_TAG = { tome: "Tome", impera: "Impera", uhlti: "U'hlti" };
function Log({ battle, mySide }) {
  const [lines, setLines] = useState([]);
  const cursor = useRef(0);
  useEffect(() => {
    const id = setInterval(() => {
      const b = battle.current; if (!b) return;
      const add = [];
      const who = (uid) => { for (const s of b.sides) for (const r of s.roster) if (r.uid === uid) return r; return null; };
      const nm = (uid) => { const r = who(uid); return r ? LINES[r.line].lord : "?"; };
      const sideOf = (uid) => { for (let i = 0; i < b.sides.length; i++) for (const r of b.sides[i].roster) if (r.uid === uid) return i === mySide; return undefined; };   // true = yours, false = theirs (only picks a banner colour)
      for (const e of b.events) {
        if (e.seq <= cursor.current) continue;
        cursor.current = e.seq;
        if (e.type === "cast") { const r = who(e.uid); add.push({ k: e.kind, mine: sideOf(e.uid), line: r && r.line, tier: r && r.tier, title: e.name, sub: `${LOG_TAG[e.kind] || "Art"} · ${nm(e.uid)}` }); }
        if (e.type === "ruined") { const r = who(e.uid); add.push({ k: "ruined", mine: sideOf(e.uid), line: r ? r.line : e.line, tier: r ? r.tier : e.tier, title: "RUINED", sub: nm(e.uid) }); }
        if (e.type === "transcend") { const r = who(e.uid); add.push({ k: "transcend", mine: sideOf(e.uid), line: r && r.line, tier: e.to, title: "TRANSCENDED", sub: `${nm(e.uid)} became ${TIER_LABEL[e.to]}` }); }
        if (e.type === "style" && b.t > 0.2) add.push({ k: "plan", title: say("seat.log.plan", { name: b.sides[e.side].name, plan: (STYLES[e.style || "balanced"] || STYLES.balanced).name }) });
        if (e.type === "clash" && e.big) add.push({ k: "clash", title: "CLASH!" });
      }
      if (add.length) setLines((l) => [...l, ...add].slice(-5));
    }, 150);
    return () => clearInterval(id);
  }, [battle]);
  return <div className="log">{lines.length > 0 && <HideBtn id="log" />}{lines.map((l, i) => <div key={i + l.title + (l.sub || "")} className={`logline ${l.k}${graceFor(bannerCfg, l.mine, null, "log") ? " graced" : ""}`} style={{ opacity: 0.35 + (i / Math.max(1, lines.length)) * 0.65, ...(graceFor(bannerCfg, l.mine, null, "log") ? { "--g": graceFor(bannerCfg, l.mine, null, "log") } : null), ...(colorFor(bannerCfg, l.k, l.mine) ? { "--c": colorFor(bannerCfg, l.k, l.mine) } : null) }}>
    {l.line && <span className="banner-art" style={{ backgroundImage: `url(${portraitUrl(l.line, l.tier || "beb")})` }} />}
    <span className="banner-text"><b>{l.title}</b>{l.sub && <em>{l.sub}</em>}</span></div>)}</div>;
}

// ── THE NAMES BAR: who is fighting whom, on top of the screen at all times ──
//  Your side is on the left (same as the plates). It reads the battle's own side names:
//  your name vs your friend / partner / the NPC — and in the League, the two NPCs.
function VsBar({ snap, mySide, spectate }) {
  const a = snap.sides[mySide], b = snap.sides[1 - mySide];
  return (
    <div className="vsbar" aria-label={`${a.name} ${say("view.vs")} ${b.name}`}>
      <span className={"vs-name " + (spectate ? "left" : "mine")} title={a.name}>{a.name}</span>
      <span className="vs-vs">{say("view.vs")}</span>
      <span className="vs-name theirs" title={b.name}>{b.name}</span>
      <HideBtn id="vs" />
    </div>
  );
}

// ── THE EYE: always there (faint), lists every piece with a hide switch ──
function EyeMenu({ view, spectate }) {
  const [open, setOpen] = useState(false);
  // only list what this fight can actually show: watchers have no action bar; fighters have no Front Row / chat
  const pieces = PIECES.filter((id) => (id === "actions" ? !spectate : (id === "front" || id === "chat") ? spectate : true));
  return (
    <div className={"hud-eye" + (view.clear ? " dim" : "") + (open ? " open" : "")}>
      <button className="eye-btn" onClick={() => setOpen((o) => !o)} title={`${say("view.menu.title")}`}>◉ {say("view.eye")}</button>
      {open && (
        <div className="eye-panel">
          <div className="eye-title">{say("view.menu.title")}</div>
          {pieces.map((id) => {
            const off = view.hidden.includes(id);
            return (
              <button key={id} className={"eye-row" + (off ? " off" : "")} onClick={() => toggle(id)} aria-pressed={!off}>
                <i>{off ? "" : "✓"}</i>{say("view.piece." + id)}
              </button>
            );
          })}
          <div className="eye-foot">
            <button onClick={toggleClear}>{view.clear ? say("view.unclear") : say("view.clear")} <kbd>H</kbd></button>
            {(view.hidden.length > 0 || view.clear) && <button onClick={showAll}>{say("view.showall")}</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Hud({ battle, mySide = 0, paused, userCam, onExit, music, toggleMusic, spectate, on }) {
  const [snap, setSnap] = useState(() => snapshot(battle.current, mySide));
  const [benchOpen, setBenchOpen] = useState(false);
  const [help, setHelp] = useState(true);
  const view = useHudView();
  const [isPaused, setPaused] = useState(false);
  const keys = useRef({ w: false, a: false, s: false, d: false });
  const pushSteerRef = useRef(() => {});

  useEffect(() => {
    const id = setInterval(() => {
      if (!battle.current) return;
      setSnap(snapshot(battle.current, mySide));
      const k = keys.current;
      if (k.w || k.a || k.s || k.d) pushSteerRef.current();
    }, 100);
    return () => clearInterval(id);
  }, [battle, mySide]);

  // paused means paused: nothing is spent, nothing is called
  const send = useCallback((cmd) => (battle.current && !paused.current ? issue(battle.current, mySide, cmd) : false), [battle, mySide, paused]);

  // camera-relative movement: W is "away from the camera" wherever it
  // has been orbited. Only direction is camera-relative — never speed.
  const pushSteer = useCallback(() => {
    const k = keys.current;
    const x = (k.d ? 1 : 0) - (k.a ? 1 : 0), z = (k.s ? 1 : 0) - (k.w ? 1 : 0);
    if (!x && !z) { send({ type: "steer", x: null }); return; }
    const yaw = userCam.current.camYaw == null ? 0 : userCam.current.camYaw;
    const c = Math.cos(yaw), sn = Math.sin(yaw);
    send({ type: "steer", x: x * c + z * sn, z: -x * sn + z * c });
  }, [send, userCam]);
  pushSteerRef.current = pushSteer;

  useEffect(() => {
    if (spectate) return undefined;
    const down = (e) => {
      const k = e.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(k)) { if (!keys.current[k]) { keys.current[k] = true; pushSteer(); } return; }
      if (k === "tab") { e.preventDefault(); if (!e.repeat) setBenchOpen((o) => !o); return; }
      if (e.repeat) return;   // ⚑ one press, one call
      if (k === " ") { e.preventDefault(); send({ type: "primera" }); return; }
      if (/^[123]$/.test(k)) { send({ type: "impera", i: +k - 1 }); return; }
      if (k === "4") { send({ type: "uhlti" }); return; }
      const ti = "zxc".indexOf(k); if (ti >= 0) { send({ type: "tome", slot: ti }); return; }
      if (k === "t") { send({ type: "transcend" }); return; }
      if (k === "escape") { setBenchOpen(false); return; }
      if (k === "g") { const s = battle.current.sides[mySide]; send({ type: "auto", on: !s.auto }); return; }
      if (k === "r") { userCam.current.yaw = 0; userCam.current.pitch = 0; userCam.current.zoom = 0; return; }
      if (k === "p") { if (battle.current && battle.current.driver) return; /* an online fight runs on the shared clock: no pause */ paused.current = !paused.current; setPaused(paused.current); return; }
      if (k === "m") { toggleMusic(); return; }
    };
    const up = (e) => { const k = e.key.toLowerCase(); if (["w", "a", "s", "d"].includes(k)) { keys.current[k] = false; pushSteer(); } };
    // cleared by MUTATION — never replace a ref a loop also reads
    const blur = () => { const kk = keys.current; kk.w = kk.a = kk.s = kk.d = false; pushSteer(); };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up); window.addEventListener("blur", blur);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("blur", blur); };
  }, [send, pushSteer, battle, mySide, paused, userCam, toggleMusic, spectate]);

  // H clears the screen (and brings it back) — fighting OR watching, never while typing in the chat box
  useEffect(() => {
    const down = (e) => {
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey || e.key.toLowerCase() !== "h") return;
      const t = e.target; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      toggleClear();
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  // LEFT CLICK on the field = Lyt Attack (its own effect, so a repaint never drops a click)
  useEffect(() => {
    if (spectate) return undefined;
    const pdown = (e) => { if (e.button === 0 && e.target && e.target.tagName === "CANVAS") send({ type: "lyt" }); };
    window.addEventListener("pointerdown", pdown);
    return () => window.removeEventListener("pointerdown", pdown);
  }, [send, spectate]);

  const me = snap.sides[mySide], them = snap.sides[1 - mySide];
  const mustChoose = !spectate && !snap.over && !me.active && me.waiting && !me.auto;

  return (
    <div className={hudClass(view)}>
      <VsBar snap={snap} mySide={mySide} spectate={!!spectate} />
      <Callouts battle={battle} mySide={mySide} />
      <Plate s={me} mine hideId="left" />
      <Plate s={them} hideId="right" />
      <div className={"clock" + (snap.overtime ? " overtime" : "")}>
        {fmtTime(snap.t)}{snap.overtime && <small> · RIFT {fmtTime(Math.max(0, snap.callTime - snap.t))}</small>}
        {RIFT_ON && !snap.overtime && snap.t > OVERTIME - 30 && <small> · rift in {Math.ceil(OVERTIME - snap.t)}</small>}
        {!RIFT_ON && snap.t > snap.callTime - 30 && !snap.over && <small> · called in {Math.max(0, Math.ceil(snap.callTime - snap.t))}</small>}
        <HideBtn id="clock" />
      </div>
      <Banners battle={battle} mySide={mySide} />
      <Log battle={battle} mySide={mySide} />
      {!spectate && <ActionBar s={me} t={snap.t} send={send} />}
      {!spectate && me.auto && !snap.over && <div className="auto-tag">AUTO — your side fights itself · {say("plan.tag", { plan: (STYLES[me.style] || STYLES.balanced).name })} · G to take it back</div>}
      {spectate && <div className="auto-tag">WATCHING — right-drag to orbit · R camera · Esc to leave</div>}
      {spectate && <FrontRow userCam={userCam} />}
      {spectate && on && <WorldChat on={on} />}
      <div className="corner">
        <button onClick={() => setBenchOpen((o) => !o)} title="Bench: switch & resurrect (Tab)">⇄</button>
        <button onClick={() => { userCam.current.yaw = 0; userCam.current.pitch = 0; userCam.current.zoom = 0; }} title="Camera default (R)">⟲</button>
        <button onClick={toggleMusic} title="Music (M)">{music ? "♪" : "♪̸"}</button>
        <button onClick={() => { paused.current = !paused.current; setPaused(paused.current); }} title="Pause (P)">{isPaused ? "▶" : "❚❚"}</button>
        <button onClick={onExit} title="Leave the duel">✕</button>
        <HideBtn id="buttons" />
      </div>
      <EyeMenu view={view} spectate={!!spectate} />
      {help && !spectate && (
        <div className="help" onClick={() => setHelp(false)}>
          <b>WASD</b> move · <b>mouse</b> aim · <b>click</b> Lyt Attack · <b>Space</b> Primera · <b>1-3</b> Impera · <b>4</b> U'hlti · <b>Z X C</b> Tomes · <b>T</b> transcend · <b>Tab</b> bench · <b>right-drag</b> camera · <b>H</b> clear screen
        </div>
      )}
      {benchOpen && !snap.over && !mustChoose && (
        <Bench s={me} title="The bench — switch in, or call one back from the Necropolis"
          onSwap={(idx) => { if (send({ type: "swap", idx })) setBenchOpen(false); }}
          onRes={(idx, to) => send({ type: "resurrect", idx, to })} onClose={() => setBenchOpen(false)}
          onPlan={(id) => { if (id !== me.style) send({ type: "style", id: id === "balanced" ? null : id }); }} />
      )}
      {mustChoose && (
        <Bench s={me} title="Your being was ruined — who goes next?" countdown={AUTO_SEND + me.pendingNext}
          onSend={(idx) => send({ type: "send", idx })} onRes={(idx, to) => send({ type: "resurrect", idx, to })} />
      )}
      {isPaused && <div className="paused">PAUSED<small>P to resume</small></div>}
    </div>
  );
}
